import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tag, Percent, Plus, Trash2, X } from 'lucide-react';
import { format } from 'date-fns';

const AdminDiscounts = ({
  discountCodes,
  newDiscountCode,
  setNewDiscountCode,
  newDiscountType,
  setNewDiscountType,
  newDiscountValue,
  setNewDiscountValue,
  newDiscountLimit,
  setNewDiscountLimit,
  newDiscountValidFrom,
  setNewDiscountValidFrom,
  newDiscountValidUntil,
  setNewDiscountValidUntil,
  onAddDiscountCode,
  onDeleteDiscountCode,
  onClose
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          MANAGE <span className="text-primary">DISCOUNT CODES</span>
        </h2>
        <Button variant="ghost" onClick={onClose} data-testid="close-discounts-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Add New Discount Code Form */}
      <form onSubmit={onAddDiscountCode} className="grid md:grid-cols-5 gap-4 mb-6">
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">Code</Label>
          <Input
            placeholder="e.g., SUMMER20"
            value={newDiscountCode}
            onChange={(e) => setNewDiscountCode(e.target.value.toUpperCase())}
            className="bg-background border-white/10"
            data-testid="new-discount-code-input"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">Type</Label>
          <select
            value={newDiscountType}
            onChange={(e) => setNewDiscountType(e.target.value)}
            className="w-full bg-background border border-white/10 rounded-sm p-2 text-white h-10"
            data-testid="discount-type-select"
          >
            <option value="percentage">Percentage (%)</option>
            <option value="fixed">Fixed (£)</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">
            {newDiscountType === 'percentage' ? 'Discount %' : 'Amount £'}
          </Label>
          <Input
            type="number"
            placeholder={newDiscountType === 'percentage' ? '0-100' : '£ amount'}
            value={newDiscountValue}
            onChange={(e) => setNewDiscountValue(e.target.value)}
            min="0"
            max={newDiscountType === 'percentage' ? '100' : undefined}
            step={newDiscountType === 'percentage' ? '1' : '0.01'}
            className="bg-background border-white/10"
            data-testid="discount-value-input"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">Usage Limit</Label>
          <Input
            type="number"
            placeholder="Unlimited"
            value={newDiscountLimit}
            onChange={(e) => setNewDiscountLimit(e.target.value)}
            min="1"
            className="bg-background border-white/10"
            data-testid="discount-limit-input"
          />
        </div>
      </form>
      
      {/* Date Range Row */}
      <form onSubmit={onAddDiscountCode} className="grid md:grid-cols-3 gap-4 mb-6 mt-4">
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">Valid From (Optional)</Label>
          <Input
            type="datetime-local"
            value={newDiscountValidFrom}
            onChange={(e) => setNewDiscountValidFrom(e.target.value)}
            className="bg-background border-white/10"
            data-testid="discount-valid-from-input"
          />
        </div>
        <div>
          <Label className="text-xs text-neutral-500 mb-1 block">Valid Until (Optional)</Label>
          <Input
            type="datetime-local"
            value={newDiscountValidUntil}
            onChange={(e) => setNewDiscountValidUntil(e.target.value)}
            className="bg-background border-white/10"
            data-testid="discount-valid-until-input"
          />
        </div>
        <div className="flex items-end">
          <Button type="submit" className="bg-success hover:bg-success/80 w-full" data-testid="add-discount-btn">
            <Plus className="w-4 h-4 mr-2" />
            CREATE CODE
          </Button>
        </div>
      </form>

      {/* Discount Codes List */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-neutral-500 mb-3">
          {discountCodes.length} Discount Code{discountCodes.length !== 1 ? 's' : ''}
        </p>
        {discountCodes.length === 0 ? (
          <p className="text-neutral-500 text-center py-8">No discount codes created yet</p>
        ) : (
          discountCodes.map((code, index) => (
            <div
              key={code.code_id}
              className="flex justify-between items-center bg-background border border-white/10 rounded-sm p-4"
              data-testid={`discount-item-${index}`}
            >
              <div className="flex items-center gap-4">
                <div className="bg-primary/20 p-2 rounded-sm">
                  {code.discount_type === 'percentage' ? (
                    <Percent className="w-5 h-5 text-primary" />
                  ) : (
                    <Tag className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-bold font-mono text-lg">{code.code}</p>
                  <p className="text-sm text-neutral-400">
                    {code.discount_type === 'percentage' 
                      ? `${code.discount_value}% off` 
                      : `£${code.discount_value} off`}
                    {code.usage_limit && ` • Limit: ${code.times_used}/${code.usage_limit} used`}
                    {!code.usage_limit && ` • Used: ${code.times_used} times`}
                  </p>
                  {(code.valid_from || code.valid_until) && (
                    <p className="text-xs text-neutral-500 mt-1">
                      {code.valid_from && `From: ${format(new Date(code.valid_from), 'MMM d, yyyy')}`}
                      {code.valid_from && code.valid_until && ' — '}
                      {code.valid_until && `Until: ${format(new Date(code.valid_until), 'MMM d, yyyy')}`}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  code.is_active ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
                }`}>
                  {code.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-error text-error hover:bg-error hover:text-white"
                  onClick={() => onDeleteDiscountCode(code.code_id, code.code)}
                  data-testid={`delete-discount-btn-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
};

export default AdminDiscounts;
